/* ------------------------ Functional imports start ------------------------ */
import { ReactNode, useState, useRef, ReactElement } from "react";
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------- Design imports start -------------------------- */
import { Box, Button, ButtonGroup, Tooltip } from "@mui/material";
import { hexToRgba } from "@uiw/react-color";
import { useTranslation } from "react-i18next";
/* --------------------------- Design imports end --------------------------- */

export type PreviewMode = 'solid' | 'xray' | 'outline'

interface Props {
  onPreviewModeChange?: (mode: PreviewMode) => void
  disabled?: boolean
}


/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function PreviewModeSelect(props: Props): ReactNode {
  /* ------------------------- Non state data start ------------------------- */
  const {
    onPreviewModeChange = () => ({}),
    disabled=false
  } = props
  const {t} = useTranslation()
  /* -------------------------- Non state data end -------------------------- */


  /* --------------------------- Flag states start -------------------------- */

  /* ---------------------------- Flag states end --------------------------- */


  /* --------------------------- Data states start -------------------------- */
  const [previewMode, setPreviewMode] = useState<PreviewMode>('solid')
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------------- Effects start ---------------------------- */

  /* ------------------------------ Effects end ----------------------------- */

  /* ------------------------ Utility functions start ----------------------- */

  /* ------------------------- Utility functions end ------------------------ */

  /* ----------------------- Callback functions start ----------------------- */
  const handlePreviewModeChange = (mode: PreviewMode): void => {
    setPreviewMode(mode)
    onPreviewModeChange(mode)
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------ Render constants start ------------------------ */
  const rgbaPrimary = hexToRgba('#16a9e4')
  const lightPrimary = `rgba(${rgbaPrimary.r}, ${rgbaPrimary.g}, ${rgbaPrimary.b}, 0.1)`
  const solidModeIcon = (
    <Box
      width='18px'
      height='18px'
      boxShadow='0 0 4px rgba(0,0,0,0.2)'
      bgcolor='white'
    ></Box>
  )
  const xRayModeIcon = (
    <Box
      width='18px'
      height='18px'
      border='1px dashed black'
      boxShadow='inset 0 0 4px rgba(0,0,0,0.1)'
      bgcolor='white'
    ></Box>
  )
  const outlinedModeIcon = (
    <Box
      width='18px'
      height='18px'
      border='2px solid #16a9e4'
      sx={{background: 'repeating-linear-gradient(45deg, white, white 3px, gray 3px, gray 4px)' }}
    ></Box>
  )
  /* ------------------------- Render constants end ------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */

  /* ------------------------ Pre render actions end ------------------------ */




  /* ------------------------------------------------------------------------ */
  /*                             Render Component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <ButtonGroup size='small' variant='outlined' disabled={disabled} sx={{borderColor: 'lightGray'}}>
      {['solid', 'xray', 'outline'].map(mode => (
        <Tooltip
          key={mode}
          arrow
          title={
            (mode==='solid' && t('imageManipulation:interaction.tooltip.previewModeSolid'))
            || (mode==='xray' && t('imageManipulation:interaction.tooltip.previewModeXray'))
            || (mode==='outline' && t('imageManipulation:interaction.tooltip.previewModeOutline'))
          }
        >
          <Button
            onClick={() => handlePreviewModeChange(mode as PreviewMode)}
            sx={{
              backgroundColor: previewMode === mode
                ? lightPrimary
                : 'white'
            }}
          >
            {mode === 'solid' && solidModeIcon}
            {mode === 'xray' && xRayModeIcon}
            {mode === 'outline' && outlinedModeIcon}
          </Button>
        </Tooltip>
      ))}
    </ButtonGroup>
  )
}
